"use client";

import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { useAuth } from "./auth-context";
import "./member-portal.css";

type Row = Record<string, any>;
type MemberData = {
  member: Row | null;
  payments: Row[];
  partners: Row[];
  offers: Row[];
  uses: Row[];
};
const empty: MemberData = {
  member: null,
  payments: [],
  partners: [],
  offers: [],
  uses: [],
};
const nav = [
  ["home", "⌂", "Início"],
  ["benefits", "◇", "Benefícios"],
  ["card", "▦", "Carteirinha"],
  ["payments", "$", "Mensalidade"],
  ["profile", "♙", "Meu perfil"],
];
const br = (value?: string) =>
  value ? value.slice(0, 10).split("-").reverse().join("/") : "—";
const money = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value || 0,
  );
const initials = (name = "AssociaPro") =>
  name
    .split(" ")
    .map((x) => x[0])
    .slice(0, 2)
    .join("");

async function loadPortal() {
  const r = await fetch("/api/member-portal.php"),
    j = await r.json();
  if (!r.ok) throw new Error(j.error || "Não foi possível carregar sua área.");
  return j as MemberData;
}

export default function MemberPortal() {
  const { user, logout } = useAuth(),
    [data, setData] = useState<MemberData>(empty),
    [view, setView] = useState("home"),
    [selected, setSelected] = useState<Row | null>(null),
    [mobile, setMobile] = useState(false),
    [loading, setLoading] = useState(true),
    [toast, setToast] = useState("");
  useEffect(() => {
    loadPortal()
      .then(setData)
      .catch((e) => alert(e.message))
      .finally(() => setLoading(false));
  }, []);
  function go(next: string) {
    setView(next);
    setSelected(null);
    setMobile(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function open(partner: Row) {
    setSelected(partner);
    setView("partner");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function notify(message: string) {
    setToast(message);
    setTimeout(() => setToast(""), 2800);
  }
  const member = data.member,
    name = member?.name || user.name;
  return (
    <div className="member-shell">
      <aside className={mobile ? "member-sidebar open" : "member-sidebar"}>
        <div className="member-brand">
          <span>●●●</span>
          <div>
            <b>
              ASSOCIA<i>PRO</i>
            </b>
            <small>CLUBE DE BENEFÍCIOS</small>
          </div>
        </div>
        <div className="member-side-person">
          <img src={member?.photo} alt="Foto do associado" />
          <div>
            <b>{name}</b>
            <small>Associado {member?.status || "Ativo"}</small>
          </div>
        </div>
        <nav>
          {nav.map((item) => (
            <button
              key={item[0]}
              className={view === item[0] ? "active" : ""}
              onClick={() => go(item[0])}
            >
              <span>{item[1]}</span>
              {item[2]}
            </button>
          ))}
        </nav>
        <div className="member-side-status">
          <span>✓</span>
          <div>
            <b>Carteirinha ativa</b>
            <small>Válida até {br(member?.due_date)}</small>
          </div>
        </div>
        <button className="member-exit" onClick={logout}>
          Sair da conta
        </button>
      </aside>
      <main className="member-workspace">
        <header>
          <button className="member-hamb" onClick={() => setMobile(!mobile)}>
            ☰
          </button>
          <div className="member-mobile-brand">
            ASSOCIA<i>PRO</i>
          </div>
          <button className="member-alert" aria-label="Notificações">
            ♢<span>2</span>
          </button>
          <img
            src={member?.photo}
            alt="Seu perfil"
            onClick={() => go("profile")}
          />
        </header>
        {loading ? (
          <div className="member-loading">
            <div />
            <p>Preparando seus benefícios...</p>
          </div>
        ) : !member ? (
          <div className="member-empty">
            <b>Cadastro não vinculado</b>
            <p>
              Fale com a associação para vincular seu acesso ao cadastro de
              membro.
            </p>
          </div>
        ) : (
          <>
            {view === "home" && <Home data={data} go={go} open={open} />}{" "}
            {view === "benefits" && <Benefits data={data} open={open} />}{" "}
            {view === "card" && <DigitalCard member={member} />}{" "}
            {view === "payments" && <Payments data={data} notify={notify} />}{" "}
            {view === "profile" && <Profile member={member} notify={notify} />}{" "}
            {view === "partner" && selected && (
              <PartnerProfile
                partner={selected}
                offers={data.offers.filter(
                  (x) => Number(x.partner_id) === Number(selected.id),
                )}
                back={() => go("benefits")}
              />
            )}
          </>
        )}
      </main>
      <nav className="member-bottom-nav">
        {nav.slice(0, 4).map((item) => (
          <button
            key={item[0]}
            className={view === item[0] ? "active" : ""}
            onClick={() => go(item[0])}
          >
            <span>{item[1]}</span>
            <small>{item[2]}</small>
          </button>
        ))}
      </nav>
      {mobile && (
        <div className="member-shade" onClick={() => setMobile(false)} />
      )}{" "}
      {toast && <div className="member-toast">✓ {toast}</div>}
    </div>
  );
}

function Home({
  data,
  go,
  open,
}: {
  data: MemberData;
  go: (x: string) => void;
  open: (x: Row) => void;
}) {
  const [slide, setSlide] = useState(0),
    partners = data.partners.filter((x) => x.cover),
    member = data.member!;
  useEffect(() => {
    if (partners.length < 2) return;
    const timer = setInterval(
      () => setSlide((x) => (x + 1) % partners.length),
      4800,
    );
    return () => clearInterval(timer);
  }, [partners.length]);
  const current = partners[slide] || data.partners[0],
    offers = data.offers.filter(
      (x) => Number(x.partner_id) === Number(current?.id),
    );
  const saved = data.uses.reduce(
    (sum, x) => sum + Number(x.discount_amount || 0),
    0,
  );
  return (
    <div className="member-page">
      <div className="member-welcome">
        <div>
          <span>Olá, {member.name.split(" ")[0]}! 👋</span>
          <h1>Seu clube de vantagens</h1>
          <p>Economize nas melhores marcas usando sua carteirinha digital.</p>
        </div>
        <button onClick={() => go("card")}>▦ Abrir carteirinha</button>
      </div>
      {current && (
        <section className="member-hero" onClick={() => open(current)}>
          <img src={current.cover} alt={current.name} />
          <div className="member-hero-shade" />
          <div className="member-hero-copy">
            <small>DESTAQUE PARA VOCÊ</small>
            <h2>{current.name}</h2>
            <p>{current.description}</p>
            <b>{offers[0]?.discount_label || current.benefit} de benefício</b>
            <button>Conhecer parceiro →</button>
          </div>
          <div className="member-dots">
            {partners.map((_, i) => (
              <button
                aria-label={`Banner ${i + 1}`}
                className={i === slide ? "active" : ""}
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  setSlide(i);
                }}
              />
            ))}
          </div>
        </section>
      )}
      <section className="member-summary-grid">
        <article>
          <span className="green">✓</span>
          <div>
            <small>Status da carteirinha</small>
            <b>Ativa</b>
            <em>até {br(member.due_date)}</em>
          </div>
        </article>
        <article>
          <span className="blue">◇</span>
          <div>
            <small>Benefícios disponíveis</small>
            <b>{data.offers.length}</b>
            <em>em {data.partners.length} parceiros</em>
          </div>
        </article>
        <article>
          <span className="orange">$</span>
          <div>
            <small>Você já economizou</small>
            <b>{money(saved || 174)}</b>
            <em>com o AssociaPro</em>
          </div>
        </article>
      </section>
      <SectionTitle
        title="Marcas parceiras"
        sub="Toque para conhecer os benefícios"
        action="Ver todas"
        click={() => go("benefits")}
      />
      <div className="member-brand-strip">
        {data.partners.map((p, i) => (
          <button key={p.id} onClick={() => open(p)}>
            <span className={`brand-bubble b${i}`}>
              {p.logo ? <img src={p.logo} alt="" /> : initials(p.name)}
            </span>
            <b>{p.name}</b>
            <small>{p.category}</small>
          </button>
        ))}
      </div>
      <SectionTitle
        title="Benefícios em destaque"
        sub="Selecionados para você"
      />
      <div className="member-offer-row">
        {data.offers.slice(0, 4).map((o) => (
          <OfferCard
            key={o.id}
            offer={o}
            partner={data.partners.find(
              (x) => Number(x.id) === Number(o.partner_id),
            )}
            open={open}
          />
        ))}
      </div>
      <section className="member-notice">
        <div>!</div>
        <p>
          <b>Lembrete de mensalidade</b>
          <span>Seu próximo vencimento é em {br(member.due_date)}.</span>
        </p>
        <button onClick={() => go("payments")}>Ver pagamento</button>
      </section>
    </div>
  );
}

function SectionTitle({
  title,
  sub,
  action,
  click,
}: {
  title: string;
  sub: string;
  action?: string;
  click?: () => void;
}) {
  return (
    <div className="member-section-title">
      <div>
        <h2>{title}</h2>
        <p>{sub}</p>
      </div>
      {action && <button onClick={click}>{action} →</button>}
    </div>
  );
}
function OfferCard({
  offer,
  partner,
  open,
}: {
  offer: Row;
  partner?: Row;
  open: (x: Row) => void;
}) {
  return (
    <article className="member-offer" onClick={() => partner && open(partner)}>
      <div>
        <img src={offer.image || partner?.cover} alt={offer.title} />
        <b>{offer.discount_label}</b>
      </div>
      <small>{partner?.name}</small>
      <h3>{offer.title}</h3>
      <p>{offer.description}</p>
      <button>Ver benefício</button>
    </article>
  );
}

function Benefits({
  data,
  open,
}: {
  data: MemberData;
  open: (x: Row) => void;
}) {
  const [q, setQ] = useState(""),
    [category, setCategory] = useState("Todos"),
    categories = ["Todos", ...new Set(data.partners.map((x) => x.category))],
    filtered = data.partners.filter(
      (p) =>
        (category === "Todos" || p.category === category) &&
        `${p.name} ${p.category} ${p.description}`
          .toLowerCase()
          .includes(q.toLowerCase()),
    );
  return (
    <div className="member-page">
      <div className="member-page-head">
        <span>CLUBE DE VANTAGENS</span>
        <h1>Encontre seu benefício</h1>
        <p>Descubra onde sua carteirinha vale mais.</p>
      </div>
      <div className="member-search">
        <span>⌕</span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar parceiro, serviço ou categoria"
        />
      </div>
      <div className="member-chips">
        {categories.map((x) => (
          <button
            key={x}
            className={x === category ? "active" : ""}
            onClick={() => setCategory(x)}
          >
            {x}
          </button>
        ))}
      </div>
      <div className="member-partner-grid">
        {filtered.map((p, i) => (
          <article key={p.id} onClick={() => open(p)}>
            <div className="member-partner-image">
              <img
                src={p.cover || "/partners/power-fit/gym-cover.webp"}
                alt={p.name}
              />
              <span>{p.benefit}</span>
            </div>
            <div className="member-partner-card-head">
              <span className={`brand-bubble b${i}`}>
                {p.logo ? <img src={p.logo} alt="" /> : initials(p.name)}
              </span>
              <div>
                <h2>{p.name}</h2>
                <p>{p.category}</p>
              </div>
            </div>
            <p>
              {p.description ||
                "Benefícios especiais para associados AssociaPro."}
            </p>
            <button>Ver perfil e descontos →</button>
          </article>
        ))}
      </div>
    </div>
  );
}

function DigitalCard({ member }: { member: Row }) {
  const [qr, setQr] = useState("");
  useEffect(() => {
    QRCode.toDataURL(String(member.card_number), {
      width: 420,
      margin: 1,
      color: { dark: "#06294b", light: "#ffffff" },
    }).then(setQr);
  }, [member.card_number]);
  return (
    <div className="member-page card-page">
      <div className="member-page-head">
        <span>ACESSO RÁPIDO</span>
        <h1>Minha carteirinha</h1>
        <p>Apresente este QR Code ao parceiro antes da compra.</p>
      </div>
      <section className="digital-card">
        <div className="digital-card-top">
          <div className="member-brand inverse">
            <span>●●●</span>
            <div>
              <b>
                ASSOCIA<i>PRO</i>
              </b>
              <small>CARTEIRINHA DIGITAL</small>
            </div>
          </div>
          <span className="card-active">● ATIVA</span>
        </div>
        <div className="digital-person">
          <img src={member.photo} alt={member.name} />
          <div>
            <small>ASSOCIADO</small>
            <h2>{member.name}</h2>
            <p>{member.category}</p>
            <b>Nº {member.card_number}</b>
          </div>
        </div>
        <div className="digital-bottom">
          <div>
            <small>VALIDADE</small>
            <b>{br(member.due_date)}</b>
          </div>
          <div>
            <small>CPF</small>
            <b>•••.{String(member.cpf).slice(-6)}</b>
          </div>
        </div>
      </section>
      <section className="qr-display">
        <div>
          {qr ? (
            <img
              src={qr}
              alt={`QR Code da carteirinha ${member.card_number}`}
            />
          ) : (
            <span>Gerando QR...</span>
          )}
        </div>
        <h2>Aproxime a câmera do parceiro</h2>
        <p>
          O código confirma seu cadastro e sua elegibilidade. Nenhum dado
          sensível é exibido.
        </p>
        <span>Carteirinha {member.card_number}</span>
      </section>
      <div className="card-security">
        <b>☀ Dica para leitura</b>
        <p>Aumente o brilho da tela e mantenha o QR Code inteiro visível.</p>
      </div>
    </div>
  );
}

function Payments({
  data,
  notify,
}: {
  data: MemberData;
  notify: (x: string) => void;
}) {
  const member = data.member!;
  return (
    <div className="member-page">
      <div className="member-page-head">
        <span>MINHA ASSOCIAÇÃO</span>
        <h1>Mensalidade</h1>
        <p>Acompanhe vencimentos e comprovantes.</p>
      </div>
      <section className="payment-reminder">
        <div>
          <small>PRÓXIMO VENCIMENTO</small>
          <h2>{br(member.due_date)}</h2>
          <p>Plano mensal • {money(120)}</p>
          <span>Carteirinha ativa</span>
        </div>
        <button
          onClick={() =>
            notify("Área preparada para futura integração com PIX e cartão.")
          }
        >
          Renovar agora
        </button>
      </section>
      <SectionTitle
        title="Histórico de pagamentos"
        sub="Últimos pagamentos registrados"
      />
      <section className="member-payment-list">
        {data.payments.map((p) => (
          <article key={p.id}>
            <span>✓</span>
            <div>
              <b>
                Mensalidade{" "}
                {new Date(p.paid_at + "T12:00:00").toLocaleDateString("pt-BR", {
                  month: "long",
                  year: "numeric",
                })}
              </b>
              <small>
                Pago em {br(p.paid_at)} • {p.method}
              </small>
            </div>
            <strong>{money(p.amount)}</strong>
            <em>Pago</em>
          </article>
        ))}
        {!data.payments.length && <p>Nenhum pagamento registrado.</p>}
      </section>
      <section className="member-help">
        <div>?</div>
        <p>
          <b>Precisa de ajuda com a mensalidade?</b>
          <span>Fale diretamente com o financeiro da associação.</span>
        </p>
        <button
          onClick={() => notify("Canal de atendimento financeiro aberto.")}
        >
          Falar com financeiro
        </button>
      </section>
    </div>
  );
}

function Profile({
  member,
  notify,
}: {
  member: Row;
  notify: (x: string) => void;
}) {
  return (
    <div className="member-page">
      <div className="member-page-head">
        <span>MEUS DADOS</span>
        <h1>Meu perfil</h1>
        <p>Confira os dados vinculados à sua associação.</p>
      </div>
      <section className="member-profile">
        <div className="profile-person">
          <img src={member.photo} alt={member.name} />
          <div>
            <h2>{member.name}</h2>
            <p>Associado desde {br(member.joined_at)}</p>
            <span>● Cadastro ativo</span>
          </div>
        </div>
        <div className="member-profile-grid">
          <Info label="Número da carteirinha" value={member.card_number} />
          <Info label="Categoria" value={member.category} />
          <Info label="Telefone" value={member.phone} />
          <Info label="WhatsApp" value={member.whatsapp} />
          <Info label="E-mail" value={member.email} />
          <Info label="Cidade" value={`${member.city}/${member.state}`} />
        </div>
        <button
          onClick={() =>
            notify("Solicitação de atualização enviada à secretaria.")
          }
        >
          Solicitar atualização cadastral
        </button>
      </section>
      <section className="member-privacy">
        <b>🔒 Seus dados estão protegidos</b>
        <p>
          Parceiros visualizam apenas nome, foto, número da carteirinha,
          validade e autorização do benefício.
        </p>
      </section>
    </div>
  );
}
function Info({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <small>{label}</small>
      <b>{value || "—"}</b>
    </div>
  );
}

function PartnerProfile({
  partner,
  offers,
  back,
}: {
  partner: Row;
  offers: Row[];
  back: () => void;
}) {
  return (
    <div className="member-page partner-public-member">
      <button className="member-back" onClick={back}>
        ← Voltar aos parceiros
      </button>
      <section className="member-public-profile">
        <div className="member-public-cover">
          <img
            src={partner.cover || "/partners/power-fit/gym-cover.webp"}
            alt={partner.name}
          />
        </div>
        <div className="member-public-identity">
          <span className="brand-bubble">
            {partner.logo ? (
              <img src={partner.logo} alt="" />
            ) : (
              initials(partner.name)
            )}
          </span>
          <div>
            <small>{partner.category}</small>
            <h1>
              {partner.name} <i>✓</i>
            </h1>
            <p>{partner.description}</p>
          </div>
          <b>{partner.benefit}</b>
        </div>
        <div className="member-public-facts">
          <span>⌖ {partner.address}</span>
          <span>◷ {partner.hours}</span>
          <span>◉ {partner.whatsapp || partner.phone}</span>
          <span>◎ {partner.instagram}</span>
        </div>
        <div className="member-public-content">
          <SectionTitle
            title="Benefícios para associados"
            sub="Apresente sua carteirinha antes da compra"
          />
          <div className="member-profile-offers">
            {offers.map((o) => (
              <article key={o.id}>
                <img src={o.image || partner.cover} alt={o.title} />
                <div>
                  <span>{o.discount_label}</span>
                  <h2>{o.title}</h2>
                  <p>{o.description}</p>
                  <small>Válido até {br(o.valid_until)}</small>
                </div>
              </article>
            ))}
          </div>
          <div className="member-conditions">
            <b>Condições de uso</b>
            <p>{partner.conditions}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
